import { abweisen, ereignis, verlangen, type Ereignis } from "../gemeinsam/index.js";
import { DJ_EREIGNIS as E } from "./ereignisse.js";
import { datum, istWildart, SICHTBARKEITEN, text, WILDARTEN, type Sichtbarkeit, type Wildart } from "./werte.js";

/**
 * Aggregat Drückjagd (Domänenmodell §5): Planung, Status und Jagdeinstellungen. Sicherheitsbestimmungen,
 * Notfallkontakte und Rettungspunkte sind bei der Anlage aus dem Revier kopiert und werden danach je Drückjagd gepflegt.
 */
export type DjStatus = "in Vorbereitung" | "bekanntgegeben" | "abgeschlossen" | "abgesagt";
export type Beteiligungsstatus = "eingeladen" | "angenommen" | "abgelehnt" | "zurückgezogen";

export interface Jagdeinstellungen {
  "Wildarten dieser Drückjagd": Wildart[];
  "Sichtbarkeit der Standbesetzung für Schützen": Sichtbarkeit;
  "Kontrollsuche nach Fehlschuss": "Standard";
  "Schießnachweis erforderlich": boolean;
  "höchstes Alter in Monaten": number | null;
}

export const JAGDEINSTELLUNGEN_VOREINSTELLUNG: Jagdeinstellungen = {
  "Wildarten dieser Drückjagd": [...WILDARTEN],
  "Sichtbarkeit der Standbesetzung für Schützen": "eigener Stand und Nachbarstände",
  "Kontrollsuche nach Fehlschuss": "Standard",
  "Schießnachweis erforderlich": false,
  "höchstes Alter in Monaten": null,
};

export interface Programmpunkt {
  Uhrzeit: string;
  "ca.": boolean;
  Programmpunkt: string;
}

export interface Notfallkontakt {
  Art: string;
  Bezeichnung: string;
  Telefonnummer: string;
  Adresse: string | null;
}

export interface Rettungspunkt {
  Kennung: string;
  Breite: number;
  Länge: number;
  Lagebeschreibung: string | null;
}

export interface DrueckjagdZustand {
  drueckjagdId: string | null;
  veranstalter: string | null;
  bezeichnung: string | null;
  jagddatum: string | null;
  treffpunkt: string | null;
  status: DjStatus | null;
  kopiertAus: string | null;
  einstellungen: Jagdeinstellungen;
  tagesablauf: Programmpunkt[];
  sicherheitsbestimmungen: string[];
  hinweise: string[];
  notfallkontakte: Notfallkontakt[];
  rettungspunkte: Rettungspunkt[];
  beteiligungen: Record<string, Beteiligungsstatus>;
  bekanntgegebenAm: Date | null;
}

export const KEINE_DRUECKJAGD: DrueckjagdZustand = {
  drueckjagdId: null, veranstalter: null, bezeichnung: null, jagddatum: null, treffpunkt: null, status: null, kopiertAus: null,
  einstellungen: JAGDEINSTELLUNGEN_VOREINSTELLUNG, tagesablauf: [], sicherheitsbestimmungen: [], hinweise: [], notfallkontakte: [],
  rettungspunkte: [], beteiligungen: {}, bekanntgegebenAm: null,
};

const EINSTELLUNGEN = Object.keys(JAGDEINSTELLUNGEN_VOREINSTELLUNG) as Array<keyof Jagdeinstellungen>;

function einstellungenAus(d: Readonly<Record<string, unknown>>): Partial<Jagdeinstellungen> {
  const aus: Record<string, unknown> = {};
  for (const k of EINSTELLUNGEN) if (d[k] !== undefined) aus[k] = d[k];
  return aus as Partial<Jagdeinstellungen>;
}

export function drueckjagdAnwenden(z: DrueckjagdZustand, e: Ereignis & { serverzeit?: Date }): DrueckjagdZustand {
  const d = e.daten;
  switch (e.typ) {
    case E.DrueckjagdAngelegt:
      return {
        ...z, veranstalter: String(d["Veranstalter"]), bezeichnung: String(d["Bezeichnung"]), jagddatum: String(d["Jagddatum"]),
        treffpunkt: String(d["Treffpunkt"]), status: "in Vorbereitung", kopiertAus: (d["kopiert aus"] as string | null) ?? null,
        einstellungen: { ...JAGDEINSTELLUNGEN_VOREINSTELLUNG, ...einstellungenAus(d) },
        sicherheitsbestimmungen: [...((d["Sicherheitsbestimmungen"] as string[] | undefined) ?? [])],
        notfallkontakte: [...((d["Notfallkontakte"] as Notfallkontakt[] | undefined) ?? [])],
        rettungspunkte: [...((d["Rettungspunkte"] as Rettungspunkt[] | undefined) ?? [])],
      };
    case E.TagesablaufGeaendert:
      return { ...z, tagesablauf: [...(d["Tagesablauf"] as Programmpunkt[])] };
    case E.SicherheitsbestimmungenGeaendert:
      return { ...z, sicherheitsbestimmungen: [...(d["Sicherheitsbestimmungen"] as string[])] };
    case E.HinweiseGeaendert:
      return { ...z, hinweise: [...(d["Hinweise"] as string[])] };
    case E.NotfallkontakteGeaendert:
      return { ...z, notfallkontakte: [...(d["Notfallkontakte"] as Notfallkontakt[])] };
    case E.RettungspunkteGeaendert:
      return { ...z, rettungspunkte: [...(d["Rettungspunkte"] as Rettungspunkt[])] };
    case E.JagdeinstellungenGeaendert:
      return { ...z, einstellungen: { ...z.einstellungen, ...einstellungenAus(d) } };
    case E.RevierBeteiligt:
      return { ...z, beteiligungen: { ...z.beteiligungen, [String(d["Revier"])]: "eingeladen" } };
    case E.BeteiligungAngenommen:
      return { ...z, beteiligungen: { ...z.beteiligungen, [String(d["Revier"])]: "angenommen" } };
    case E.BeteiligungAbgelehnt:
      return { ...z, beteiligungen: { ...z.beteiligungen, [String(d["Revier"])]: "abgelehnt" } };
    case E.BeteiligungZurueckgezogen:
      return { ...z, beteiligungen: { ...z.beteiligungen, [String(d["Revier"])]: "zurückgezogen" } };
    case E.DrueckjagdBekanntgegeben:
      return { ...z, status: "bekanntgegeben", bekanntgegebenAm: e.serverzeit ?? (d["Zeitpunkt"] as Date | undefined) ?? null };
    case E.DrueckjagdAbgeschlossen:
      return { ...z, status: "abgeschlossen" };
    case E.DrueckjagdAbgesagt:
      return { ...z, status: "abgesagt" };
    default:
      return z;
  }
}

/** Reviere, deren Einteilung zur Drückjagd gehört: Veranstalter und Reviere mit angenommener Beteiligung. */
export function beteiligteReviere(z: DrueckjagdZustand): string[] {
  const r = z.veranstalter ? [z.veranstalter] : [];
  return [...r, ...Object.entries(z.beteiligungen).filter(([, s]) => s === "angenommen").map(([id]) => id)];
}

export function istBekanntgegeben(z: DrueckjagdZustand): boolean {
  return z.status === "bekanntgegeben" || z.status === "abgeschlossen";
}

/** Reviere, in denen die handelnde Person Teilnehmer ist (von der Anwendung über die Zuordnungen beantwortet). */
export type TeilnehmerIn = (revierId: string) => boolean;

/**
 * Zugriff auf einen Befehl, der dem Veranstalter vorbehalten ist (TESTSPRACHE §4, H4): Teilnehmer des Veranstalters
 * gehen weiter zur Rollenprüfung; Teilnehmer eines angenommenen beteiligten Reviers → `nicht-veranstalter`; alle
 * anderen → `kein-zugriff` (auch eingeladene, abgelehnte und zurückgezogene Reviere, N-20, N-21).
 */
export function veranstalterbefehlZugreifen(z: DrueckjagdZustand, teilnehmerIn: TeilnehmerIn): void {
  if (!z.veranstalter) abweisen("kein-zugriff");
  if (teilnehmerIn(z.veranstalter)) return;
  if (beteiligteReviere(z).some((r) => r !== z.veranstalter && teilnehmerIn(r))) abweisen("nicht-veranstalter");
  abweisen("kein-zugriff");
}

/**
 * Zugriff auf das Annehmen oder Ablehnen einer Beteiligung (F-200): Zugriff hat, wer Teilnehmer eines Reviers der
 * Drückjagd oder des Reviers der Beteiligung ist; ohne Beteiligung des Reviers gibt es nichts zu beantworten
 * (`kein-zugriff`). Wer nicht Teilnehmer des eingeladenen Reviers ist, dem fehlt die Rolle (`rolle-fehlt`); ob er dort
 * Jagdherr ist, prüft die Anwendung danach. Liefert das Revier der Beteiligung.
 */
export function beteiligungsantwortZugreifen(z: DrueckjagdZustand, revierId: string | undefined, teilnehmerIn: TeilnehmerIn): string {
  const rv = revierId && z.beteiligungen[revierId] ? revierId : undefined;
  const zugriff = [...(rv ? [rv] : []), ...beteiligteReviere(z)].some(teilnehmerIn);
  if (!zugriff || !rv) abweisen("kein-zugriff");
  if (!teilnehmerIn(rv)) abweisen("rolle-fehlt");
  return rv;
}

function nichtAbgeschlossen(z: DrueckjagdZustand): void {
  if (z.status === "abgeschlossen") abweisen("drueckjagd-abgeschlossen");
}

export interface Revierkopie {
  sicherheitsbestimmungen: string[];
  notfallkontakte: Notfallkontakt[];
  rettungspunkte: Rettungspunkt[];
}

export interface Anlage {
  veranstalter: string;
  bezeichnung: unknown;
  jagddatum: unknown;
  treffpunkt: unknown;
  kopie: Revierkopie;
  kopiertAus: string | null;
  einstellungen?: Jagdeinstellungen;
  handelnd: string;
}

/** Drückjagd anlegen bzw. kopieren (F-050, F-052, F-063): Status „in Vorbereitung“, Kopie der Revierinhalte. */
export function drueckjagdAnlegen(a: Anlage): Ereignis {
  const jagddatum = datum(a.jagddatum, "jagddatum-fehlt");
  const treffpunkt = text(a.treffpunkt);
  verlangen(treffpunkt, "treffpunkt-fehlt");
  const bezeichnung = text(a.bezeichnung);
  verlangen(bezeichnung, "bezeichnung-fehlt");
  return ereignis(E.DrueckjagdAngelegt, {
    Veranstalter: a.veranstalter, Bezeichnung: bezeichnung, Jagddatum: jagddatum, Treffpunkt: treffpunkt, "Drückjagdstatus": "in Vorbereitung",
    ...(a.einstellungen ?? JAGDEINSTELLUNGEN_VOREINSTELLUNG),
    "kopiert aus": a.kopiertAus,
    Sicherheitsbestimmungen: a.kopie.sicherheitsbestimmungen, Notfallkontakte: a.kopie.notfallkontakte, Rettungspunkte: a.kopie.rettungspunkte,
    "handelnde Person": a.handelnd,
  });
}

const UHRZEIT = /^([01]\d|2[0-3]):[0-5]\d$/;

export function tagesablaufPflegen(z: DrueckjagdZustand, liste: unknown, handelnd: string): Ereignis[] {
  nichtAbgeschlossen(z);
  if (!Array.isArray(liste)) abweisen("unzulaessige-angaben");
  const punkte: Programmpunkt[] = liste.map((p: Record<string, unknown>) => {
    const uhrzeit = text(p?.["Uhrzeit"]);
    const titel = text(p?.["Programmpunkt"]);
    if (!uhrzeit || !UHRZEIT.test(uhrzeit) || !titel) abweisen("unzulaessige-angaben");
    return { Uhrzeit: uhrzeit, "ca.": p["ca."] === true, Programmpunkt: titel };
  });
  return [ereignis(E.TagesablaufGeaendert, { Tagesablauf: punkte, "handelnde Person": handelnd })];
}

function textliste(liste: unknown): string[] {
  if (!Array.isArray(liste)) abweisen("unzulaessige-angaben");
  return liste.map((t) => {
    const s = text(t);
    verlangen(s, "unzulaessige-angaben");
    return s;
  });
}

export function sicherheitsbestimmungenPflegen(z: DrueckjagdZustand, liste: unknown, handelnd: string): Ereignis[] {
  nichtAbgeschlossen(z);
  return [ereignis(E.SicherheitsbestimmungenGeaendert, { Sicherheitsbestimmungen: textliste(liste), "handelnde Person": handelnd })];
}

export function hinweisePflegen(z: DrueckjagdZustand, liste: unknown, handelnd: string): Ereignis[] {
  nichtAbgeschlossen(z);
  return [ereignis(E.HinweiseGeaendert, { Hinweise: textliste(liste), "handelnde Person": handelnd })];
}

const TELEFON = /^\+?[0-9][0-9 /-]{1,24}$/;

export function notfallkontaktePruefen(liste: unknown): Notfallkontakt[] {
  if (!Array.isArray(liste)) abweisen("unzulaessige-angaben");
  return liste.map((k: Record<string, unknown>) => {
    const art = text(k?.["Art"]);
    const bezeichnung = text(k?.["Bezeichnung"]);
    const telefon = text(k?.["Telefonnummer"]);
    if (!art || !bezeichnung) abweisen("unzulaessige-angaben");
    if (!telefon || !TELEFON.test(telefon)) abweisen("telefonnummer-ungueltig");
    return { Art: art, Bezeichnung: bezeichnung, Telefonnummer: telefon, Adresse: text(k["Adresse"]) };
  });
}

export function notfallkontaktePflegen(z: DrueckjagdZustand, liste: unknown, handelnd: string): Ereignis[] {
  nichtAbgeschlossen(z);
  const neu = notfallkontaktePruefen(liste);
  for (const alt of z.notfallkontakte.filter((k) => k.Art === "Notruf")) {
    if (!neu.some((k) => k.Art === "Notruf" && k.Telefonnummer === alt.Telefonnummer)) abweisen("notruf-nicht-aenderbar");
  }
  return [ereignis(E.NotfallkontakteGeaendert, { Notfallkontakte: neu, "handelnde Person": handelnd })];
}

export function rettungspunktePruefen(liste: unknown): Rettungspunkt[] {
  if (!Array.isArray(liste)) abweisen("unzulaessige-angaben");
  const gesehen = new Set<string>();
  return liste.map((p: Record<string, unknown>) => {
    const kennung = text(p?.["Kennung"]);
    verlangen(kennung, "kennung-fehlt");
    if (gesehen.has(kennung)) abweisen("rettungspunkt-kennung-vergeben");
    gesehen.add(kennung);
    const b = Number(p["Breite"]);
    const l = Number(p["Länge"]);
    if (!Number.isFinite(b) || !Number.isFinite(l) || Math.abs(b) > 90 || Math.abs(l) > 180) abweisen("position-ungueltig");
    return { Kennung: kennung, Breite: b, Länge: l, Lagebeschreibung: text(p["Lagebeschreibung"]) };
  });
}

export function rettungspunktePflegen(z: DrueckjagdZustand, liste: unknown, handelnd: string): Ereignis[] {
  nichtAbgeschlossen(z);
  return [ereignis(E.RettungspunkteGeaendert, { Rettungspunkte: rettungspunktePruefen(liste), "handelnde Person": handelnd })];
}

/** Jagdeinstellungen ändern (Wertobjekt Jagdeinstellungen): nur geänderte Einstellungen stehen im Ereignis. */
export function jagdeinstellungenAendern(z: DrueckjagdZustand, aenderung: Record<string, unknown>, handelnd: string): Ereignis[] {
  nichtAbgeschlossen(z);
  const neu: Partial<Jagdeinstellungen> = {};
  if (aenderung["Wildarten dieser Drückjagd"] !== undefined) {
    const w = aenderung["Wildarten dieser Drückjagd"];
    if (!Array.isArray(w) || w.length === 0 || !w.every(istWildart)) abweisen("unzulaessige-angaben");
    neu["Wildarten dieser Drückjagd"] = WILDARTEN.filter((x) => (w as string[]).includes(x));
  }
  const s = aenderung["Sichtbarkeit der Standbesetzung für Schützen"];
  if (s !== undefined) {
    if (!(SICHTBARKEITEN as readonly unknown[]).includes(s)) abweisen("standsichtbarkeit-unbekannt");
    neu["Sichtbarkeit der Standbesetzung für Schützen"] = s as Sichtbarkeit;
  }
  const k = aenderung["Kontrollsuche nach Fehlschuss"];
  if (k !== undefined) {
    if (k !== "Standard") abweisen("kontrollsuche-ist-standard");
    neu["Kontrollsuche nach Fehlschuss"] = "Standard";
  }
  const erforderlichRoh = aenderung["Schießnachweis erforderlich"];
  // Ja/Nein als Boolean (README §4.3); die Darstellung „ja“/„nein“ wird ebenso angenommen
  const erforderlich = erforderlichRoh === "ja" ? true : erforderlichRoh === "nein" ? false : erforderlichRoh;
  if (erforderlich !== undefined) {
    if (typeof erforderlich !== "boolean") abweisen("unzulaessige-angaben");
    neu["Schießnachweis erforderlich"] = erforderlich;
  }
  const alter = aenderung["höchstes Alter in Monaten"];
  if (alter !== undefined && alter !== null) {
    if (typeof alter !== "number" || !Number.isInteger(alter) || alter < 1) abweisen("hoechstes-alter-ungueltig");
    neu["höchstes Alter in Monaten"] = alter;
  }
  const ergebnis = { ...z.einstellungen, ...neu };
  if (ergebnis["Schießnachweis erforderlich"] && ergebnis["höchstes Alter in Monaten"] === null) abweisen("hoechstes-alter-fehlt");
  if (Object.keys(neu).length === 0) return [];
  return [ereignis(E.JagdeinstellungenGeaendert, { ...neu, "handelnde Person": handelnd })];
}

export function bekanntgeben(z: DrueckjagdZustand, handelnd: string, jetzt: Date): Ereignis[] {
  if (z.status !== "in Vorbereitung") abweisen("statuswechsel-unzulaessig");
  return [ereignis(E.DrueckjagdBekanntgegeben, { "Drückjagdstatus": "bekanntgegeben", Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}

/** Abschluss nur aus „bekanntgegeben“ und nur, wenn die Jagd ruht (Invariante Drückjagd, ADR-0017). */
export function abschliessen(z: DrueckjagdZustand, jagdRuht: boolean, handelnd: string, jetzt: Date): Ereignis[] {
  if (z.status !== "bekanntgegeben") abweisen("statuswechsel-unzulaessig");
  if (!jagdRuht) abweisen("jagd-im-gange");
  return [ereignis(E.DrueckjagdAbgeschlossen, { "Drückjagdstatus": "abgeschlossen", Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}

export function absagen(z: DrueckjagdZustand, handelnd: string, jetzt: Date): Ereignis[] {
  if (z.status !== "in Vorbereitung" && z.status !== "bekanntgegeben") abweisen("statuswechsel-unzulaessig");
  return [ereignis(E.DrueckjagdAbgesagt, { "Drückjagdstatus": "abgesagt", Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}

export function revierBeteiligen(z: DrueckjagdZustand, revierId: string, handelnd: string, jetzt: Date): Ereignis[] {
  nichtAbgeschlossen(z);
  if (revierId === z.veranstalter) abweisen("veranstalter-nicht-beteiligbar");
  const s = z.beteiligungen[revierId];
  if (s === "eingeladen" || s === "angenommen") abweisen("revier-bereits-beteiligt");
  return [ereignis(E.RevierBeteiligt, { Revier: revierId, Beteiligungsstatus: "eingeladen", Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}

export function beteiligungBeantworten(z: DrueckjagdZustand, revierId: string, annehmen: boolean, handelnd: string, jetzt: Date): Ereignis[] {
  if (z.beteiligungen[revierId] !== "eingeladen") abweisen("statuswechsel-unzulaessig");
  return [ereignis(annehmen ? E.BeteiligungAngenommen : E.BeteiligungAbgelehnt, { Revier: revierId, Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}

export function beteiligungZurueckziehen(z: DrueckjagdZustand, revierId: string, handelnd: string, jetzt: Date): Ereignis[] {
  const s = z.beteiligungen[revierId];
  if (s !== "eingeladen" && s !== "angenommen") abweisen("statuswechsel-unzulaessig");
  return [ereignis(E.BeteiligungZurueckgezogen, { Revier: revierId, "vorheriger Status": s, Zeitpunkt: jetzt, "handelnde Person": handelnd })];
}
